'use client';

import { useMemo, useState } from 'react';

import type { JsonValue } from '@lotushealth/lotushealth_js_sdk/schemas';

import type {
  CodedPrescriptionRequestT,
  EditPrescriptionRequestT,
  MedicationStatusTInput,
} from '@lotushealth/lotushealth_js_sdk/schemas';

import {
  PriorAuthAttachmentsList,
  usePriorAuthActionMutation,
  usePriorAuthDetailsQuery,
} from '@/entities/prior-auth';
import {
  useEditPrescriptionMutation,
  usePharmacyByIdQuery,
  usePrescriptionQuery,
} from '@/entities/rx';
import { RxDraftSummaryNode, openRxWizardOverlay } from '@/features/rx-form';
import { useProposedActionMutations } from '@/features/proposed-actions-plan/model/useProposedActionMutations';
import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/shadcn/alert';
import { Badge } from '@/shared/ui/shadcn/badge';
import { Button } from '@/shared/ui/shadcn/button';
import { Input } from '@/shared/ui/shadcn/input';
import { Label } from '@/shared/ui/shadcn/label';
import { useCopilotOverlays } from '@/shared/stores/ui';
import { logger } from '@/shared/lib';

import type { PaCompletedRxViewPrefill } from '../model/paCompletedRxViewPrefill';

const APPROVED_STATUS = 'Approved';
const PARTIALLY_APPROVED_STATUS = 'Partially Approved';

// `SENT_TO_PHARMACY` is a locally-advanced terminal written by the backend
// after `POST /dosespot/prescriptions/send` succeeds on top of an already
// cleared PA. The underlying payer approval is still in force (the auth
// number is preserved), but the clinician has no further action to take
// here — the Rx is already on its way to the pharmacy.
const SENT_TO_PHARMACY_STATUS = 'Sent to Pharmacy';

function toText(value: string | number | null | undefined, fallback = ''): string {
  if (value == null) {
    return fallback;
  }
  return String(value);
}

/**
 * Read-only view of the Rx associated with a completed Prior Authorization.
 *
 * Opened when:
 *
 * - A clinician clicks a terminal PA case in the :class:`PatientPriorAuthsTab`.
 * - A ``review_completed_prior_auth`` proposed action executes.
 *
 * The authorization number lives at the top as a copyable read-only field —
 * that's the only value the clinician typically needs to transcribe into
 * the payer's system or a downstream workflow.
 *
 * We fetch both the DoseSpot prescription (to render ``RxDraftSummaryNode``)
 * and the PA case details (to show status metadata) in parallel. Errors on
 * either side fall back to a muted placeholder rather than blocking the
 * entire view.
 */
interface PaCompletedRxViewProps {
  prefill: PaCompletedRxViewPrefill;
}

export function PaCompletedRxView({ prefill }: PaCompletedRxViewProps) {
  const overlays = useCopilotOverlays();
  const prescriptionQuery = usePrescriptionQuery(
    prefill.patientUserId,
    prefill.dosespotPrescriptionId
  );
  const detailsQuery = usePriorAuthDetailsQuery(prefill.priorAuthRecordId, {
    refetchInterval: 30_000,
  });
  const mutations = useProposedActionMutations();
  const priorAuthActionMutation = usePriorAuthActionMutation();
  const editPrescriptionMutation = useEditPrescriptionMutation();
  const [sendError, setSendError] = useState<string | null>(null);

  const prescription = prescriptionQuery.data ?? null;
  const dosespotCase = detailsQuery.data?.dosespotCase?.case ?? null;

  const authorizationNumber = useMemo(() => {
    const fromDetails = dosespotCase?.FinalStatus?.AuthorizationNumber ?? null;
    return fromDetails ?? prefill.authorizationNumber ?? '';
  }, [dosespotCase, prefill.authorizationNumber]);

  const statusLabel = prefill.status;

  const isApproved = prefill.status === APPROVED_STATUS;
  const isPartiallyApproved = prefill.status === PARTIALLY_APPROVED_STATUS;
  const isSentToPharmacy = prefill.status === SENT_TO_PHARMACY_STATUS;
  const canSendToPharmacy = isApproved || isPartiallyApproved;

  const finalStatus = dosespotCase?.FinalStatus ?? null;
  const isExpedited = dosespotCase?.IsExpedited === true;

  const medicationDisplayName = toText(prescription?.DisplayName, '—');
  const directions = toText(prescription?.Directions, '—');

  const approvedQuantity = prefill.approvedQuantity ?? finalStatus?.Quantity ?? null;
  const approvedDaysSupply = prefill.approvedDaysSupply ?? finalStatus?.DaysSupply ?? null;
  const approvedRefills = prefill.approvedRefills ?? finalStatus?.NumberOfRefills ?? null;

  const quantity =
    isPartiallyApproved && approvedQuantity ? approvedQuantity : toText(prescription?.Quantity, '');
  const refills =
    isPartiallyApproved && approvedRefills ? approvedRefills : toText(prescription?.Refills, '');
  const daysSupply =
    isPartiallyApproved && approvedDaysSupply
      ? approvedDaysSupply
      : toText(prescription?.DaysSupply, '');
  const prescriptionEffectiveDate = toText(prescription?.EffectiveDate, '');
  const pharmacyNotes = prescription?.PharmacyNotes
    ? toText(prescription.PharmacyNotes)
    : undefined;
  const noSubstitutions = prescription?.NoSubstitutions === true;

  const pharmacyId = prescription?.PharmacyId ? String(prescription.PharmacyId) : undefined;
  const pharmacyQuery = usePharmacyByIdQuery(pharmacyId);
  const pharmacyDisplayName = pharmacyQuery.data?.StoreName
    ? String(pharmacyQuery.data.StoreName)
    : undefined;

  const diagnosis = useMemo(() => {
    const primary = prescription?.FirstPrescriptionDiagnosis?.PrimaryDiagnosis;
    if (!primary?.DiagnosisCode) {
      return null;
    }
    return {
      code: String(primary.DiagnosisCode),
      name: primary.DiagnosisDescription ? String(primary.DiagnosisDescription) : '',
    };
  }, [prescription]);

  const copyAuthorizationNumber = async () => {
    if (!authorizationNumber) {
      return;
    }
    try {
      await navigator.clipboard.writeText(authorizationNumber);
    } catch (err) {
      logger.warn('paCompletedRx.copyAuthNumber.failed', { err });
    }
  };

  const finalizeProposedAction = async () => {
    const source = prefill.sourceProposedAction;
    if (!source) {
      return;
    }
    const toolOutput: Record<string, JsonValue> = {
      priorAuthRecordId: prefill.priorAuthRecordId as JsonValue,
    };
    if (authorizationNumber) {
      toolOutput.authorizationNumber = authorizationNumber as JsonValue;
    }
    await mutations.finalizeFromSource(source, toolOutput).catch((err: unknown) => {
      logger.error('paCompletedRx.finalizeFromSource.failed', {
        externalId: source.externalId,
        err,
      });
    });
  };

  const acknowledge = async () => {
    await finalizeProposedAction();
    overlays.close('priorAuthCompletedRx');
  };

  const sendToPharmacy = async () => {
    if (!prefill.dosespotPrescriptionId) {
      setSendError('No prescription id available to send.');
      return;
    }
    setSendError(null);
    try {
      const response = await priorAuthActionMutation.mutateAsync({
        recordId: prefill.priorAuthRecordId,
        patientUserId: prefill.patientUserId,
        request: { action: 'send_prescription' },
      });
      if (response.outcome !== 'APPLIED' && response.outcome !== 'APPLIED_LOCAL_SYNC_PENDING') {
        setSendError(response.rejectionCode ?? 'The send is still awaiting confirmation.');
        return;
      }

      await finalizeProposedAction();
      overlays.close('priorAuthCompletedRx');
    } catch (err) {
      logger.error('paCompletedRx.sendToPharmacy.failed', {
        dosespotPrescriptionId: prefill.dosespotPrescriptionId,
        err,
      });
      setSendError(err instanceof Error ? err.message : 'Failed to send to pharmacy.');
    }
  };

  const reviewAndSendPartialApproval = async () => {
    if (!prescription) {
      return;
    }
    if (!prefill.dosespotPrescriptionId) {
      setSendError('No prescription id available.');
      return;
    }

    const dispensableDrugId = prescription.DispensableDrugId;
    const dispenseUnitId = prescription.DispenseUnitId;
    const rxDirections = prescription.Directions;
    const rxPharmacyId = prescription.PharmacyId;
    const effectiveDate = prescription.EffectiveDate;
    const medicationStatus = prescription.MedicationStatus;

    if (
      dispensableDrugId == null ||
      dispenseUnitId == null ||
      !rxDirections ||
      rxPharmacyId == null ||
      !effectiveDate ||
      !medicationStatus
    ) {
      setSendError('Prescription is missing required fields for editing.');
      return;
    }

    setSendError(null);

    const adjustedQuantity = approvedQuantity
      ? Number(approvedQuantity)
      : Number(prescription.Quantity);
    const adjustedRefills = approvedRefills
      ? Number(approvedRefills)
      : Number(prescription.Refills);
    const adjustedDaysSupply = approvedDaysSupply
      ? Number(approvedDaysSupply)
      : prescription.DaysSupply != null
        ? Number(prescription.DaysSupply)
        : undefined;

    const prescriptionData: CodedPrescriptionRequestT = {
      DispensableDrugId: Number(dispensableDrugId),
      DispenseUnitId: Number(dispenseUnitId),
      Directions: String(rxDirections),
      PharmacyId: Number(rxPharmacyId),
      EffectiveDate: String(effectiveDate),
      Status: medicationStatus as MedicationStatusTInput,
      Quantity: adjustedQuantity,
      Refills: adjustedRefills,
      DaysSupply: adjustedDaysSupply,
      PharmacyNotes:
        prescription.PharmacyNotes != null ? String(prescription.PharmacyNotes) : undefined,
      NoSubstitutions:
        prescription.NoSubstitutions != null ? Boolean(prescription.NoSubstitutions) : undefined,
    };

    const editRequest: EditPrescriptionRequestT = { prescriptionData };

    try {
      await editPrescriptionMutation.mutateAsync({
        dosespotPrescriptionId: prefill.dosespotPrescriptionId,
        data: editRequest,
        userId: prefill.patientUserId,
      });
    } catch (err) {
      logger.error('paCompletedRx.editPrescription.failed', {
        dosespotPrescriptionId: prefill.dosespotPrescriptionId,
        err,
      });
      setSendError(
        err instanceof Error ? err.message : 'Failed to update prescription with approved values.'
      );
      return;
    }

    overlays.close('priorAuthCompletedRx');
    openRxWizardOverlay(overlays, {
      kind: 'coded',
      dispensableDrugId: Number(dispensableDrugId),
      directions: String(rxDirections),
      quantity: String(adjustedQuantity),
      refills: String(adjustedRefills),
      daysSupply: String(adjustedDaysSupply ?? ''),
      pharmacyNotes:
        prescription.PharmacyNotes != null ? String(prescription.PharmacyNotes) : undefined,
      noSubstitutions: prescription.NoSubstitutions === true,
      effectiveDate: String(effectiveDate),
      existingDosespotPrescriptionId: prefill.dosespotPrescriptionId,
      sourceProposedAction: prefill.sourceProposedAction,
    });
  };

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto p-3">
      <header className="space-y-1">
        <div className="flex items-center gap-2">
          <div className="text-sm font-semibold">
            {isSentToPharmacy
              ? 'Prescription Sent'
              : isPartiallyApproved
                ? 'Partially Approved Prescription'
                : 'Approved Prescription'}
          </div>
          <Badge variant="secondary" className="rounded-full text-[10px]">
            {statusLabel || 'Completed'}
          </Badge>
          {isExpedited ? (
            <Badge
              variant="outline"
              className="rounded-full border-orange-400/50 bg-orange-50 text-[10px] text-orange-700 dark:border-orange-500/30 dark:bg-orange-950/40 dark:text-orange-300"
            >
              Expedited
            </Badge>
          ) : null}
        </div>
        <p className="text-xs text-muted-foreground">
          {isSentToPharmacy
            ? 'Prescription has been sent to the pharmacy — authorization number preserved for reference.'
            : isPartiallyApproved
              ? 'Prior authorization is partially approved with adjusted quantities. Review the payer-approved values below and send to the pharmacy.'
              : isApproved
                ? 'Prior authorization is approved — send the prescription to the pharmacy.'
                : 'Prior authorization is complete. Record the authorization number with the pharmacy or payer if needed.'}
        </p>
      </header>

      <section className="space-y-2 rounded-md border border-border bg-muted/40 p-3">
        <Label className="text-xs">Authorization number</Label>
        <div className="flex items-center gap-2">
          <Input
            readOnly
            value={authorizationNumber}
            placeholder="— not issued —"
            className="h-8 font-mono text-xs"
          />
          <Button
            size="sm"
            variant="outline"
            onClick={() => void copyAuthorizationNumber()}
            disabled={!authorizationNumber}
            className="text-xs"
          >
            Copy
          </Button>
        </div>
      </section>

      {prescriptionQuery.isLoading ? (
        <div className="text-xs text-muted-foreground">Loading prescription…</div>
      ) : prescriptionQuery.isError ? (
        <Alert variant="destructive">
          <AlertTitle>Could not load prescription</AlertTitle>
          <AlertDescription className="text-xs">
            {prescriptionQuery.error instanceof Error
              ? prescriptionQuery.error.message
              : 'Unknown error loading prescription.'}
          </AlertDescription>
        </Alert>
      ) : (
        <section aria-label="Prescription summary" className="rounded-md border border-border p-3">
          <RxDraftSummaryNode
            medicationDisplayName={medicationDisplayName}
            directions={directions}
            quantity={quantity}
            refills={refills}
            daysSupply={daysSupply}
            effectiveDate={prescriptionEffectiveDate}
            pharmacyDisplayName={pharmacyDisplayName}
            pharmacyNotes={pharmacyNotes}
            noSubstitutions={noSubstitutions}
            diagnosis={diagnosis}
          />
        </section>
      )}

      {(dosespotCase?.Attachments?.length ?? 0) > 0 ? (
        <section aria-label="Attachments" className="rounded-md border border-border p-3">
          <PriorAuthAttachmentsList
            recordId={prefill.priorAuthRecordId}
            attachments={dosespotCase?.Attachments ?? []}
          />
        </section>
      ) : null}

      {sendError ? (
        <Alert variant="destructive">
          <AlertTitle>Could not send to pharmacy</AlertTitle>
          <AlertDescription className="text-xs">{sendError}</AlertDescription>
        </Alert>
      ) : null}

      <div className="mt-auto flex justify-end gap-2 pt-2">
        <Button
          size="sm"
          variant="outline"
          onClick={() => overlays.close('priorAuthCompletedRx')}
          className="text-xs"
          disabled={priorAuthActionMutation.isPending || editPrescriptionMutation.isPending}
        >
          Close
        </Button>
        {canSendToPharmacy && !isSentToPharmacy ? (
          isPartiallyApproved ? (
            <Button
              size="sm"
              onClick={() => void reviewAndSendPartialApproval()}
              disabled={
                editPrescriptionMutation.isPending ||
                !prefill.dosespotPrescriptionId ||
                prescriptionQuery.isLoading ||
                detailsQuery.isLoading ||
                (!approvedQuantity && !approvedDaysSupply && !approvedRefills)
              }
              className="text-xs"
            >
              {editPrescriptionMutation.isPending ? 'Updating…' : 'Review & send'}
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={() => void sendToPharmacy()}
              disabled={
                priorAuthActionMutation.isPending ||
                !prefill.dosespotPrescriptionId ||
                prescriptionQuery.isLoading
              }
              className="text-xs"
            >
              {priorAuthActionMutation.isPending ? 'Sending…' : 'Send to pharmacy'}
            </Button>
          )
        ) : isSentToPharmacy ? null : (
          <Button size="sm" onClick={() => void acknowledge()} className="text-xs">
            Acknowledge
          </Button>
        )}
      </div>
    </div>
  );
}
